import {
  bool,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from "prop-types";
import { forwardRef } from "react";

import styled from "styled-components/macro";
import { height } from "theme";

const alignItems = ({ $topAlign, $bottomAlign }) => {
  if ($topAlign) return `align-items: flex-start;`;
  if ($bottomAlign) return `align-items: flex-end;`;
  return `align-items: center;`;
};

const SDiv = styled("div")`
  ${({
    $gap,
    $rowGap,
    $centered,
    $fullWidth,
    $fullHeight,
    $flexGrow,
    $justifyContent,
    $reverse,
    $wrap,
    $inline,
  }) => `
  display: ${$inline ? "inline-flex" : "flex"};
  flex-direction: ${$reverse ? "row-reverse" : "row"};
  ${
    $wrap ? `flex-wrap: ${$wrap === "reverse" ? "wrap-reverse" : "wrap"};` : ""
  };
  gap: ${$gap};
  ${$rowGap ? `row-gap: ${$rowGap};` : ""}
  ${$centered ? "justify-content: center;" : ""};
  ${$fullWidth ? "justify-content: space-between; flex-grow: 1;" : ""}
  ${$flexGrow ? "flex-grow: 1;" : ""}
  ${$justifyContent ? `justify-content: ${$justifyContent};` : ""}
  `}
  ${alignItems}
  ${height}
  transition: gap .3s cubic-bezier(0.68, -0.55, 0.27, 1.55);
`;

const FnrHorizontalGap = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {number} param0.gap - changes the gap property. defaults to 16.
   * @param {boolean} param0.centered - applies `justify-content: center;`
   * @param {boolean} param0.fullWidth - forces div to fill the width of its container
   * @param {boolean} param0.fullHeight - forces div to fill the remaining height of its container (usually FnrVerticalGap)
   * @param {boolean} param0.flexGrow - applies `flex-grow: 1;`
   * @param {string} param0.justifyContent - use this if centered and fullwidth aren't quite doing the job you need.
   * @param {object} param0.style - don't use this unless you're really really in a hurry.
   * @param {boolean} param0.topAlign - aligns all elements to the top.
   * @param {boolean} param0.bottomAlign - aligns all elements to the bottom.
   * @param {boolean} param0.reverse - if you need things to appear in reverse order for some situations
   * @param {boolean|"reverse"} param0.wrap - allows things to wrap (FnrTabs, for example)
   * @param {number} param0.rowGap - sets the gap between rows, only really useful when wrap is set to "wrap"
   * @param {boolean} param0.inline - use inline-flex instead flex
   * @param {import("react").RefObject} ref
   * @returns
   */
  (
    {
      gap: gapArg = 16,
      centered,
      fullWidth,
      fullHeight,
      flexGrow,
      justifyContent,
      style,
      children,
      topAlign,
      bottomAlign,
      reverse,
      wrap,
      rowGap: rowGapArg,
      inline,
      ...rest
    },
    ref
  ) => {
    const gap = typeof gapArg === "number" ? `${gapArg}px` : gapArg;
    const rowGap = typeof rowGapArg === "number" ? `${rowGapArg}px` : rowGapArg;
    return (
      <SDiv
        $gap={gap}
        $centered={centered}
        $fullWidth={fullWidth}
        $noHeight={!fullHeight}
        $justifyContent={justifyContent}
        $topAlign={topAlign}
        $bottomAlign={bottomAlign}
        $reverse={reverse}
        $wrap={wrap}
        $rowGap={rowGap}
        $flexGrow={flexGrow}
        style={style}
        ref={ref}
        $inline={inline}
        {...rest}
      >
        {children}
      </SDiv>
    );
  }
);

FnrHorizontalGap.propTypes = {
  gap: oneOfType([number, string]),
  centered: bool,
  fullWidth: bool,
  fullHeight: bool,
  flexGrow: bool,
  justifyContent: string,
  style: object,
  topAlign: bool,
  bottomAlign: bool,
  reverse: bool,
  wrap: oneOfType([bool, oneOf(["reverse"])]),
  rowGap: oneOfType([number, string]),
  children: node,
  inline: bool,
};

export default FnrHorizontalGap;
